import React, { useState, useRef, useEffect, useCallback } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import PublicMapPage from './components/public/PublicMapPage';
import AuthWrapper from './components/AuthWrapper';
import type { MapboxMapHandle } from './components/MapboxMap';
import mapboxgl from 'mapbox-gl';
import './App.css';
import './components/MapStyles.css';
import './components/MapboxPopup.css';

import { Box, createTheme, ThemeProvider, CssBaseline, Dialog } from '@mui/material';
import Header from './components/Header';

// COMMENTED OUT: Authentication is disabled
// import LoginPage from './components/auth/LoginPage';
// import { AuthProvider, useAuth } from './components/auth/AuthContext';
import { MapProvider, useMap } from './context/MapContext';

import AboutModal from './components/AboutModal';
// COMMENTED OUT: Admin features disabled
// import AdminPanel from './components/AdminPanel';
// import AdminDashboard from './components/AdminDashboard';
import CoordinateSearch from './components/CoordinateSearch';
import FileImport from './components/FileImport';
import LayerControls from './components/LayerControls';
import { permanentLayers } from './data/layersConfig';
import { permanentLayersGeojson } from './data/permanentLayersGeojson';
import { buildFeatureIndex } from './utils/featureIndex';
import { FeatureIndexItem } from './components/FeatureSearch';
import MapboxMap from './components/MapboxMap';


import { parseFile, parseCSVWithMapping } from './import/parsers';
import { v4 as uuidv4 } from 'uuid';
import ColumnMappingDialog from './components/ColumnMappingDialog';
import 'mapbox-gl/dist/mapbox-gl.css';

import MapPdfExport from './components/MapPdfExport';
import MeasureWidget from './components/MeasureWidget';
import { useMapboxDraw } from './components/useMapboxDraw';

declare global {
  interface Window {
    _leaflet_map?: mapboxgl.Map;
    userEmail?: string;
  }
}

const darkTheme = createTheme({
  typography: {
    fontFamily: "'Louguiya', Arial, sans-serif"
  },
  palette: {
    mode: 'dark',
    primary: { main: '#fff' },
    background: { default: '#181a20', paper: '#232634' },
    text: { primary: '#f4f6fa', secondary: '#bdbdbd' },
  },
});

interface ImportedLayer {
  id: string;
  name: string;
  geojson: GeoJSON.FeatureCollection;
  visible: boolean;
}



// Main App component that requires authentication
const MainApp = (): React.ReactElement => {

  // ... (rest of state and hooks)
  // Fix: Handler to close measure tool
  const handleMeasureClose = useCallback(() => {
    setMeasureActive(false);
  }, []);

  const mapboxMapRef = useRef<MapboxMapHandle>(null);

  // --- Permanent GeoJSON layer state ---
  const [permanentGeojsons, setPermanentGeojsons] = useState<Array<{ id: string; name: string; geojson: GeoJSON.FeatureCollection }>>([]);

  // Reference to the Mapbox map instance
  const mapRef = useRef<mapboxgl.Map | null>(null);

  // File import refs
  const shpImportRef = useRef<{ open: () => void }>(null);
  const kmlImportRef = useRef<{ open: () => void }>(null);
  const gpxImportRef = useRef<{ open: () => void }>(null);
  const coordinateSearchRef = useRef<HTMLDivElement>(null);
  const csvImportRef = useRef<{ open: () => void }>(null);

  // About modal state
  const [aboutOpen, setAboutOpen] = useState(false);
  // Admin panel state
  const [adminPanelOpen, setAdminPanelOpen] = useState(false);
  const [dashboardOpen, setDashboardOpen] = useState(false);
  // Current zoom level
  const [currentZoom, setCurrentZoom] = useState(6);
  // Measure tool state
  const [measureActive, setMeasureActive] = useState(false);
  const [measureMode, setMeasureMode] = useState<'line' | 'polyline' | 'area'>('line');
  const [measureResult, setMeasureResult] = useState<string | null>(null);

  // Find XY popup state
  const [findXYActive, setFindXYActive] = useState(false);

  // Zoom handler states
  const [scrollZoomEnabled, setScrollZoomEnabled] = useState(true);

  const [layerActionPanelOpen, setLayerActionPanelOpen] = useState(false);


  // Add this state variable
  const [showAboutOnLogin, setShowAboutOnLogin] = useState(false); // Set to false to disable

  // COMMENTED OUT: Authentication is disabled
  // const { user, logout } = useAuth();
  const { visibleLayers, labelsVisible, setLabelsVisible } = useMap();

  // COMMENT OUT OR REMOVE THIS useEffect TO DISABLE AUTO-SHOWING THE MODAL
  // useEffect(() => {
  //   if (user && !aboutOpen && showAboutOnLogin) {
  //     setAboutOpen(true);
  //   }
  // }, [user, aboutOpen, showAboutOnLogin]);

  // Toast state
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const [toastType, setToastType] = useState<'success' | 'error'>('success');
  const [toastOpen, setToastOpen] = useState(false);

  // Column mapping dialog state
  const [showColumnMapping, setShowColumnMapping] = useState(false);
  const [pendingCSVFile, setPendingCSVFile] = useState<File | null>(null);
  const [csvHeaders, setCSVHeaders] = useState<string[]>([]);
  const [csvSampleData, setCSVSampleData] = useState<any[]>([]);

  // Show toast function
  const showToast = (message: string, type: 'success' | 'error' = 'success') => {
    setToastMessage(message);
    setToastType(type);
    setToastOpen(true);
    setTimeout(() => {
      setToastOpen(false);
      setToastMessage(null);
    }, 3000);
  };

  // --- Handle coordinate search ---
  const handleCoordinateSearch = (lat: number, lng: number) => {
    if (mapRef.current) {
      mapRef.current.flyTo({ center: [lng, lat], zoom: 15 });
    }
  };

  // --- Handle pin management ---
  const [pins, setPins] = useState<{ lat: string; lng: string; id: string; coordType?: string; zone?: string; easting?: string; northing?: string; }[]>([]);
  const [tempMarker, setTempMarker] = useState<{ lat: number; lng: number } | null>(null);
  const [isPinMode, setIsPinMode] = useState(false);

  const handlePin = (point: { lat: string; lng: string; id: string; coordType?: string; zone?: string; easting?: string; northing?: string; }) => {
    const newPin = { ...point, id: uuidv4() };
    setPins(prev => [...prev, newPin]);
  };

  const handleRemovePin = (id: string) => {
    setPins(prev => prev.filter(pin => pin.id !== id));
  };

  const handleClearPins = () => {
    setPins([]);
  };

  // Handle map clicks for PIN mode
  const handleMapClick = (e: mapboxgl.MapMouseEvent) => {
    if (isPinMode) {
      const { lat, lng } = e.lngLat;
      handlePin({ lat: lat.toFixed(6), lng: lng.toFixed(6), id: '' });
    }
  };



  // --- Handle file import ---
  const [importedLayers, setImportedLayers] = useState<ImportedLayer[]>([]);
  const [importedFileName, setImportedFileName] = useState<string | null>(null);
  const [showImportedFilePopup, setShowImportedFilePopup] = useState(false);
  const [selectedImportedLayerId, setSelectedImportedLayerId] = useState<string | null>(null);

  const handleFileImport = (file: File) => {
    parseFile(file).then((result) => {
      if (result.needsManualMapping && result.headers && result.sampleData) {
        // Show column mapping dialog for CSV files that need manual mapping
        setPendingCSVFile(file);
        setCSVHeaders(result.headers);
        setCSVSampleData(result.sampleData);
        setShowColumnMapping(true);
        return;
      }

      if (result.error) {
        showToast(`Error importing ${file.name}: ${result.error}`, 'error');
        return;
      }

      const newLayer: ImportedLayer = {
        id: uuidv4(),
        name: file.name,
        geojson: result.geojson,
        visible: true,
      };

      setImportedLayers(prev => [...prev, newLayer]);
      setImportedFileName(file.name);
      showToast(`Successfully imported ${file.name} with ${result.featureCount} features`);
    }).catch((error) => {
      showToast(`Error importing ${file.name}: ${error.message}`, 'error');
    });
  };

  // Handle column mapping confirmation
  const handleColumnMappingConfirm = async (latColumn: string, lngColumn: string) => {
    if (!pendingCSVFile) return;

    try {
      const result = await parseCSVWithMapping(pendingCSVFile, latColumn, lngColumn);

      if (result.error) {
        showToast(`Error importing ${pendingCSVFile.name}: ${result.error}`, 'error');
        return;
      }

      const newLayer: ImportedLayer = {
        id: uuidv4(),
        name: pendingCSVFile.name,
        geojson: result.geojson,
        visible: true,
      };

      setImportedLayers(prev => [...prev, newLayer]);
      setImportedFileName(pendingCSVFile.name);
      showToast(`Successfully imported ${pendingCSVFile.name} with ${result.featureCount} features`);
    } catch (error) {
      showToast(`Error importing ${pendingCSVFile.name}: ${error}`, 'error');
    } finally {
      // Reset state
      setPendingCSVFile(null);
      setCSVHeaders([]);
      setCSVSampleData([]);
    }
  };

  // Handle column mapping dialog close
  const handleColumnMappingClose = () => {
    setShowColumnMapping(false);
    setPendingCSVFile(null);
    setCSVHeaders([]);
    setCSVSampleData([]);
  };

  // Handle imported layer toggle
  const handleImportedLayerToggle = (layerId: string) => {
    setImportedLayers(prev =>
      prev.map(layer =>
        layer.id === layerId
          ? { ...layer, visible: !layer.visible }
          : layer
      )
    );
  };

  // Handle imported layer delete
  const handleImportedLayerDelete = (layerId: string) => {
    setImportedLayers(prev => prev.filter(layer => layer.id !== layerId));
    showToast('Layer removed successfully');
  };

  const handleMenuSelect = (key: string) => {
    const refs: { [key: string]: React.RefObject<{ open: () => void }> } = {
      importCSV: csvImportRef,
      importSHP: shpImportRef,
      importKML: kmlImportRef,
      importGPX: gpxImportRef,
    };
    if (key === 'findCoordinates') {
      setShowCoordinateSearch(true);
    } else if (refs[key]) {
      refs[key]?.current?.open();
    }
  };

  // Drag and drop handlers
  const [isDragOver, setIsDragOver] = useState(false);

  const handleDragOver = (e: React.DragEvent) => {
    // Don't interfere with text input elements
    const target = e.target as HTMLElement;
    if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' ||
      target.closest('.MuiInputBase-root') || target.closest('.MuiTextField-root')) {
      return;
    }

    // Only prevent default and stop propagation if files are being dragged
    if (e.dataTransfer.types.includes('Files')) {
      e.preventDefault();
      e.stopPropagation();
      setIsDragOver(true);
    }
  };

  const handleDragLeave = (e: React.DragEvent) => {
    // Only prevent default and stop propagation if files are being dragged
    if (e.dataTransfer.types.includes('Files')) {
      e.preventDefault();
      e.stopPropagation();
      // Only hide feedback if we're leaving the main container
      if (!e.currentTarget.contains(e.relatedTarget as Node)) {
        setIsDragOver(false);
      }
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    // Don't interfere with text input elements
    const target = e.target as HTMLElement;
    if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' ||
      target.closest('.MuiInputBase-root') || target.closest('.MuiTextField-root')) {
      return;
    }

    // Only prevent default and stop propagation if files are being dropped
    if (e.dataTransfer?.files && e.dataTransfer.files.length > 0) {
      e.preventDefault();
      e.stopPropagation();
      setIsDragOver(false);

      const files = Array.from(e.dataTransfer.files);

      if (files.length === 1) {
        // Single file - import directly
        handleFileImport(files[0]);
      } else {
        // Multiple files - import the first one and show message
        handleFileImport(files[0]);
        showToast(`Imported ${files[0].name}. Multiple files detected - only the first file was imported.`, 'success');
      }

      e.dataTransfer.clearData();
    }
  };

  // --- Handle feature selection from search ---
  const handleFeatureSelect = (feature: FeatureIndexItem) => {
    if (!feature || !feature.geometry) return;
    if (mapRef.current) {
      // Handle Polygon/MultiPolygon with centroid
      if (
        feature.geometry.type === 'Polygon' ||
        feature.geometry.type === 'MultiPolygon'
      ) {
        try {
          if (feature.bounds) {
            const [[minLng, minLat], [maxLng, maxLat]] = feature.bounds;
            mapRef.current.fitBounds([[minLng, minLat], [maxLng, maxLat]], { padding: 80, maxZoom: 17 });
          }
        } catch (e) {
          // fallback to bounds if centroid fails
          if (feature.bounds) {
            const [[minLng, minLat], [maxLng, maxLat]] = feature.bounds;
            mapRef.current.fitBounds([[minLng, minLat], [maxLng, maxLat]], { padding: 80, maxZoom: 17 });
          }
        }
      } else if (feature.geometry.type === 'Point') {
        const [lng, lat] = feature.geometry.coordinates;
        setFlyToTarget({ lat, lng });
        setFlyToZoom(18); // Zoom close for points
        setFlyToTrigger(prev => !prev);
      } else if (feature.bounds) {
        // fallback for other geometry types
        const [[minLng, minLat], [maxLng, maxLat]] = feature.bounds;
        mapRef.current.fitBounds([[minLng, minLat], [maxLng, maxLat]], { padding: 80, maxZoom: 17 });
      }
    }
  };

  // --- Build feature index for search bar ---
  const featureIndex: FeatureIndexItem[] = buildFeatureIndex({
    permanentGeojsons,
    importedLayers
  });

  // Fetch permanent GeoJSONs on mount
  useEffect(() => {
    let mounted = true;

    const fetchLayers = async () => {
      const results = [];

      for (const layer of permanentLayersGeojson) {
        // Handle group layers
        if (layer.type === 'group' && layer.children) {
          for (const childLayer of layer.children) {
            try {
              if (!childLayer.url) {
                console.error(`No URL found for child layer ${childLayer.id}`);
                continue;
              }
              const resp = await fetch(childLayer.url);
              const geojson = await resp.json();
              results.push({ id: childLayer.id, name: childLayer.name, geojson });
            } catch (error) {
              console.error(`Failed to load child layer ${childLayer.name}:`, error);
            }
          }
        } else {
          // Handle regular layers
          try {
            if (!layer.url) {
              console.error(`No URL found for layer ${layer.id}`);
              continue;
            }
            const resp = await fetch(layer.url);
            const geojson = await resp.json();
            results.push({ id: layer.id, name: layer.name, geojson });
          } catch (error) {
            console.error(`Failed to load layer ${layer.name}:`, error);
          }
        }
      }

      if (mounted) {
        setPermanentGeojsons(results);
      }
    };

    fetchLayers();
  }, []);

  // Fly-to functionality
  const [flyToTarget, setFlyToTarget] = useState<{ lat: number; lng: number } | null>(null);
  const [flyToTrigger, setFlyToTrigger] = useState(false);
  const [flyToZoom, setFlyToZoom] = useState<number | null>(null);

  // Manual geometry for Area mode
  const [manualGeometry, setManualGeometry] = useState<GeoJSON.Geometry | null>(null);
  const [manualZoomRequest, setManualZoomRequest] = useState(0);

  // Area markers for Area mode
  const [areaMarkers, setAreaMarkers] = useState<Array<{ lat: number; lng: number; id: string }>>([]);

  // Coordinate search state
  const [showCoordinateSearch, setShowCoordinateSearch] = useState(false);

  // Measure states
  const [measureResults, setMeasureResults] = useState<Array<{ distance?: number; area?: number; coordinates: number[][] }>>([]);
  const [isDrawing, setIsDrawing] = useState(false);
  const [distanceHasStarted, setDistanceHasStarted] = useState(false);
  const [liveMeasurement, setLiveMeasurement] = useState<number | null>(null);

  // Initialize MapboxDraw
  const { startDrawing, clearDrawing } = useMapboxDraw({
    getMapInstance: () => mapRef.current,
    measureMode,
    onMeasureResult: setMeasureResults,
    isDrawing,
    distanceHasStarted,
    onDrawingChange: setIsDrawing,
    onDistanceStartedChange: setDistanceHasStarted,
    onLiveMeasurement: setLiveMeasurement
  });

  const handleMeasureToggle = () => {
    setMeasureActive(!measureActive);
    if (measureActive) {
      clearDrawing();
    }
  };

  const handleMeasureModeChange = (newMode: 'line' | 'polyline' | 'area') => {
    setMeasureMode(newMode);
    clearDrawing();
  };

  const handleMeasureClear = () => {
    clearDrawing();
  };

  // Zoom handler functions
  const handleScrollZoomToggle = () => {
    setScrollZoomEnabled(!scrollZoomEnabled);
  };



  // Start drawing when measure is activated
  useEffect(() => {
    if (measureActive && mapRef.current) {
      startDrawing();
    }
  }, [measureActive, mapRef.current, startDrawing]);

  // PDF Export state
  const [pdfExportOpen, setPdfExportOpen] = useState(false);
  const mapContainerRef = useRef<HTMLDivElement>(null);

  // Pinned points for PDF export
  const [pinnedPoints, setPinnedPoints] = useState<Array<{ lat: number; lng: number; label?: string }>>([]);

  // Measure results for PDF export
  const [measureResultsForPdf, setMeasureResultsForPdf] = useState<Array<{ distance?: number; area?: number; coordinates: number[][] }>>([]);

  const handleManualZoomComplete = () => {
    // Reset the manual zoom request counter after zoom is complete
    setManualZoomRequest(0);
  };

  const handleAreaMarkersChange = (markers: Array<{ lat: number; lng: number; id: string }>) => {
    setAreaMarkers(markers);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
      <Header
        onMenuSelect={handleMenuSelect}
        onToggleLabels={setLabelsVisible}
        labelsVisible={labelsVisible}
        onToggleMeasure={handleMeasureToggle}
        measureActive={measureActive}
        onToggleFindXY={() => setShowCoordinateSearch(!showCoordinateSearch)}
        findXYActive={findXYActive}
        onAboutClick={() => setAboutOpen(true)}
        onClearCsv={() => { }} // This is now handled by the FileImport component
        featureIndex={featureIndex}
        onFeatureSelect={handleFeatureSelect}
        measureMode={measureMode}
        onMeasureModeChange={handleMeasureModeChange}
        // COMMENTED OUT: Authentication is disabled - using default values
        // onLogout={logout}
        // userEmail={user?.username || 'N/A'}
        onLogout={() => { }}
        userEmail="Guest"
        permanentLayers={permanentLayers}
        onHomeClick={() => {
          // Mauritania bounding box: southwest [lng, lat], northeast [lng, lat]
          const mauritaniaBounds: [[number, number], [number, number]] = [
            [-17.06, 14.72], // SW corner
            [-4.82, 27.30],  // NE corner
          ];
          if (mapRef.current) {
            mapRef.current.fitBounds(mauritaniaBounds, { padding: 20, maxZoom: 6 });
          }
        }}
        handleFileImport={handleFileImport}
        // COMMENTED OUT: Admin features disabled
        // onAdminClick={() => setAdminPanelOpen(true)}
        // onDashboardClick={() => setDashboardOpen(true)}
        // isAdmin={user?.role === 'admin'}
        onAdminClick={() => { }}
        onDashboardClick={() => { }}
        isAdmin={false}
        pdfExportOpen={pdfExportOpen}
        setPdfExportOpen={setPdfExportOpen}
        mapContainerRef={mapContainerRef}
        measureResults={measureResults}
        pinnedPoints={pinnedPoints}
        scrollZoomEnabled={scrollZoomEnabled}
        onToggleScrollZoom={handleScrollZoomToggle}
        onLayerActionClick={() => setLayerActionPanelOpen(true)}
      />
      <div
        style={{ position: 'relative', flex: 1 }}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <AboutModal open={aboutOpen} onClose={() => setAboutOpen(false)} />
        {/* COMMENTED OUT: Admin features disabled */}
        {/* <Dialog 
          open={adminPanelOpen} 
          onClose={() => setAdminPanelOpen(false)}
          maxWidth="lg"
          fullWidth
          PaperProps={{
            sx: {
              height: '90vh',
              maxHeight: '90vh'
            }
          }}
        >
          <AdminPanel />
        </Dialog>
        
        <AdminDashboard
          open={dashboardOpen}
          onClose={() => setDashboardOpen(false)}
        /> */}
        {showCoordinateSearch && (
          <CoordinateSearch
            ref={coordinateSearchRef}
            onFind={handleCoordinateSearch}
            onClose={() => setShowCoordinateSearch(false)}
            onTempMarker={setTempMarker}
            pins={pins}
            onPin={handlePin}
            onRemovePin={handleRemovePin}
            onClearPins={handleClearPins}
            onPinModeChange={setIsPinMode}
            onManualGeometryChange={setManualGeometry}
            onManualZoomRequest={() => setManualZoomRequest(prev => prev + 1)}
            onAreaMarkersChange={handleAreaMarkersChange}
          />
        )}

        <LayerControls
          layers={permanentLayers}
          importedLayers={importedLayers}
          onImportedLayerToggle={handleImportedLayerToggle}
          onImportedLayerDelete={handleImportedLayerDelete}
        />
        <MapboxMap
          ref={mapboxMapRef}
          layers={permanentLayers}
          center={[-10.95, 21.0]}
          zoom={currentZoom}
          importedLayers={importedLayers}
          measureActive={measureActive}
          measureMode={measureMode}
          onModeChange={handleMeasureModeChange}
          findXYActive={findXYActive}
          pins={pins}
          tempMarker={tempMarker}
          areaMarkers={areaMarkers}
          onMapLoad={(map) => { mapRef.current = map; }}
          onZoomChange={setCurrentZoom}
          flyToTarget={flyToTarget}
          flyToTrigger={flyToTrigger}
          flyToZoom={flyToZoom}
          showImportedFilePopup={showImportedFilePopup}
          selectedImportedLayerId={selectedImportedLayerId}
          onLayerSelect={() => { }}
          onPopupClose={() => { }}
          onMeasureClose={handleMeasureClose}
          onMapClick={handleMapClick}
          manualGeometry={manualGeometry}
          manualZoomRequest={manualZoomRequest}
          permanentLayers={permanentLayers}
          visibleLayers={visibleLayers}
          labelsVisible={labelsVisible}
          onManualZoomComplete={handleManualZoomComplete}
          scrollZoomEnabled={scrollZoomEnabled}
          layerActionPanelOpen={layerActionPanelOpen}
          onLayerActionClose={() => setLayerActionPanelOpen(false)}
        // Remove mapContainerRef from here
        />
        {measureActive && (
          <MeasureWidget
            mode={measureMode}
            onModeChange={handleMeasureModeChange}
            measureResults={measureResults}
            onClear={handleMeasureClear}
            onClose={handleMeasureToggle}
            isDrawing={isDrawing}
            distanceHasStarted={distanceHasStarted}
            liveMeasurement={liveMeasurement}
          />
        )}
        <FileImport
          ref={shpImportRef}
          accept=".zip,.shp"
          label="Import Shapefile"
          onFileSelected={handleFileImport}
          enableGlobalDrop={false}
        />
        <FileImport
          ref={kmlImportRef}
          accept=".kml"
          label="Import KML"
          onFileSelected={handleFileImport}
          enableGlobalDrop={false}
        />
        <FileImport
          ref={gpxImportRef}
          accept=".gpx"
          label="Import GPX"
          onFileSelected={handleFileImport}
          enableGlobalDrop={false}
        />
        <FileImport
          ref={csvImportRef}
          accept=".csv"
          label="Import CSV"
          onFileSelected={handleFileImport}
          enableGlobalDrop={false}
        />

        {toastMessage && toastOpen && (
          <div style={{
            position: 'fixed',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            background: toastType === 'error' ? 'rgba(211, 47, 47, 0.95)' : 'rgba(76, 175, 80, 0.95)',
            color: 'white',
            padding: '16px 24px',
            borderRadius: 12,
            zIndex: 2100,
            boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
            backdropFilter: 'blur(8px)',
            border: `1px solid ${toastType === 'error' ? 'rgba(255,255,255,0.2)' : 'rgba(255,255,255,0.1)'}`,
            fontSize: '14px',
            fontWeight: 500,
            maxWidth: '450px',
            textAlign: 'center',
            lineHeight: 1.4,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '12px'
          }}>
            <span style={{ flex: 1 }}>{toastMessage}</span>
            <button
              onClick={() => {
                setToastOpen(false);
                setToastMessage(null);
              }}
              style={{
                background: 'none',
                border: 'none',
                color: 'white',
                fontSize: '18px',
                fontWeight: 'bold',
                cursor: 'pointer',
                padding: '4px',
                borderRadius: '4px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                minWidth: '24px',
                height: '24px',
                opacity: 0.8,
                transition: 'opacity 0.2s'
              }}
              onMouseEnter={(e) => e.currentTarget.style.opacity = '1'}
              onMouseLeave={(e) => e.currentTarget.style.opacity = '0.8'}
            >
              ×
            </button>
          </div>
        )}

        {/* Drag overlay */}
        {isDragOver && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(44, 143, 219, 0.15)',
            backdropFilter: 'blur(8px)',
            zIndex: 2000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'white',
            pointerEvents: 'none',
          }}>
            <div style={{
              background: 'rgba(24, 29, 38, 0.95)',
              backdropFilter: 'blur(15px)',
              borderRadius: '16px',
              border: '2px dashed #2c8fdb',
              padding: '40px 60px',
              textAlign: 'center',
              boxShadow: '0 12px 40px rgba(0, 0, 0, 0.4)',
              animation: 'pulse 2s infinite',
            }}>
              <div style={{
                fontSize: '48px',
                marginBottom: '16px',
                color: '#2c8fdb',
              }}>
                📁
              </div>
              <div style={{
                fontSize: '24px',
                fontWeight: 'bold',
                marginBottom: '8px',
                fontFamily: 'Louguiya, sans-serif',
              }}>
                Drop your file here
              </div>
              <div style={{
                fontSize: '16px',
                color: 'rgba(255, 255, 255, 0.8)',
                fontFamily: 'Louguiya, sans-serif',
              }}>
                Supports CSV, KML, GPX, SHP, GeoJSON
              </div>
            </div>
          </div>
        )}
      </div>
      <MapPdfExport
        open={pdfExportOpen}
        onClose={() => setPdfExportOpen(false)}
        mapContainerRef={mapContainerRef}
        visibleLayers={visibleLayers}
        measureResults={measureResults}
        pinnedPoints={pinnedPoints}
      />

      {/* Column Mapping Dialog */}
      <ColumnMappingDialog
        open={showColumnMapping}
        onClose={handleColumnMappingClose}
        onConfirm={handleColumnMappingConfirm}
        headers={csvHeaders}
        sampleData={csvSampleData}
      />
    </Box>
  );
};



const App = () => {
  return (
    <ThemeProvider theme={darkTheme}>
      <CssBaseline />
      <Router>
        <Routes>
          <Route path="/public" element={
            <MapProvider>
              <PublicMapPage />
            </MapProvider>
          } />
          <Route path="*" element={
            <AuthWrapper>
              <MapProvider>
                <MainApp />
              </MapProvider>
            </AuthWrapper>
          } />
        </Routes>
      </Router>
    </ThemeProvider>
  );
};

// COMMENTED OUT: Authentication check is disabled
// const AuthenticatedApp = () => {
//   const { isAuthenticated, loading } = useAuth();

//   if (loading) {
//     return (
//       <div style={{ 
//         minHeight: '100vh', 
//         background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
//         display: 'flex',
//         justifyContent: 'center',
//         alignItems: 'center',
//         color: 'white',
//         fontFamily: 'Arial, sans-serif'
//       }}>
//         <div style={{ textAlign: 'center' }}>
//           <h2>Loading...</h2>
//         </div>
//       </div>
//     );
//   }

//   return isAuthenticated ? <MainApp /> : <LoginPage />;
// };

export default App;
